import React, { useState, useMemo } from 'react';
import {
  UserPlus,
  Search,
  SlidersHorizontal,
  MapPin,
  Calendar,
  MoreVertical,
  Phone,
  Mail,
  FileText,
  Activity,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Archive,
  User,
  Key,
  Navigation
} from 'lucide-react';
import { formatAddress, getNavigationLinks, isValidAddress } from '../../utils/addressFormatter';
import { motion, AnimatePresence } from 'framer-motion';
import ClientAgendaView from '../ClientAgendaView';
import ChangeBeneficiaryStatusModal from '../modals/ChangeBeneficiaryStatusModal';
import { BeneficiaryDocumentGenerator } from '../beneficiaryDocuments';
import { BeneficiaryAssessmentWizard } from '../beneficiaryAssessment';
import { getBeneficiaryStatusService } from '../../services/beneficiaryStatusService';
import { BENEFICIARY_STATUS, STATUS_LABELS } from '../../constants/beneficiaryStatuses';
import { useToastContext } from '../../contexts/ToastContext';

/**
 * Premium Card for Beneficiaries
 */
const PremiumBeneficiaryCard = ({ client, onAction, hasPermission }) => {
  const [showMenu, setShowMenu] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case BENEFICIARY_STATUS.ACTIVE: return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case BENEFICIARY_STATUS.PENDING: return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case BENEFICIARY_STATUS.SUSPENDED: return 'bg-rose-500/20 text-rose-400 border-rose-500/30';
      case BENEFICIARY_STATUS.INACTIVE: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
      case BENEFICIARY_STATUS.ARCHIVED: return 'bg-slate-700/20 text-slate-500 border-slate-700/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getStatusLabel = (status, isActive) => {
    if (status && STATUS_LABELS[status]) return STATUS_LABELS[status];
    if (isActive === false) return 'Inactif';
    return 'Actif';
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`group relative bg-white/80 dark:bg-slate-800/40 backdrop-blur-md border border-gray-200 dark:border-slate-700/50 rounded-2xl transition-all duration-300 ${showMenu ? 'z-50' : 'z-0'}`}
      style={{
        borderColor: client.color ? `${client.color}30` : undefined, // 30 = approx 20% opacity
      }}
    >
      {/* Decorative gradient header using client color */}
      <div
        className="h-2 w-full opacity-60 group-hover:opacity-100 transition-opacity rounded-t-2xl"
        style={{
          background: client.color
            ? `linear-gradient(to right, ${client.color}, ${client.color}cc)`
            : 'linear-gradient(to right, #10b981, #14b8a6)'
        }}
      />

      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-4">
            <div
              className={`relative w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold text-white shadow-inner ${!client.photoUrl && !client.color ? 'bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800 border border-gray-300 dark:border-slate-600' : ''}`}
              style={!client.photoUrl && client.color ? { backgroundColor: client.color, textShadow: '0 2px 4px rgba(0,0,0,0.3)' } : {}}
            >
              {client.photoUrl ? (
                <img src={client.photoUrl} alt="Avatar" className="w-full h-full object-cover rounded-2xl" />
              ) : (
                <span>{client.firstName?.[0]}{client.lastName?.[0]}</span>
              )}
              {/* Type Badge (small icon) */}
              <div className="absolute -bottom-2 -right-2 bg-white dark:bg-slate-900 rounded-full p-1 border border-gray-200 dark:border-slate-700">
                <User size={12} className="text-gray-600 dark:text-slate-300" style={{ color: client.color }} />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 transition-colors" style={{ color: showMenu ? client.color : undefined }}>
                {client.firstName} {client.lastName}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(client.status || (client.isActive === false ? BENEFICIARY_STATUS.INACTIVE : BENEFICIARY_STATUS.ACTIVE))}`}>
                  {getStatusLabel(client.status, client.isActive)}
                </span>

                {client.hasAuthAccess && (
                  <span className="text-xs px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 flex items-center gap-1">
                    <Key size={10} /> Portail
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 rounded-lg text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors"
            >
              <MoreVertical size={20} />
            </button>

            {/* Context Menu - Simplified */}
            <AnimatePresence>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-20 py-2 overflow-hidden"
                  >
                    <div className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider bg-gray-50 dark:bg-slate-800/50 sticky top-0">Actions Rapides</div>

                    <button onClick={() => { onAction('view', client); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700/50 hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center gap-2 transition-colors">
                      <User size={16} className="text-gray-400 dark:text-slate-500" /> Voir la fiche
                    </button>

                    {hasPermission({ resource: 'beneficiaries', action: 'write' }) && (
                      <button onClick={() => { onAction('edit', client); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700/50 hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center gap-2 transition-colors">
                        <Edit size={16} className="text-gray-400 dark:text-slate-500" /> Modifier
                      </button>
                    )}

                    <button onClick={() => { onAction('changeStatus', client); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700/50 hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center gap-2 transition-colors">
                      <Activity size={16} className="text-gray-400 dark:text-slate-500" /> Changer Statut
                    </button>

                    <button onClick={() => { onAction('schedule', client); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700/50 hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center gap-2 transition-colors">
                      <Calendar size={16} className="text-gray-400 dark:text-slate-500" /> Planifier
                    </button>

                    <button onClick={() => { onAction('nfc', client); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700/50 hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center gap-2 transition-colors">
                      <CheckCircle size={16} className="text-gray-400 dark:text-slate-500" /> Configurer NFC
                    </button>

                    <div className="h-px bg-gray-200 dark:bg-slate-700 my-1 mx-2"></div>

                    <button onClick={() => { onAction('admin-panel', client); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 text-sky-400 hover:bg-sky-500/10 flex items-center gap-2 transition-colors font-medium">
                      <SlidersHorizontal size={16} /> Plus d'options...
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-2 mb-6">
          <div className="flex items-start gap-3 p-2 rounded-lg bg-gray-50 dark:bg-slate-900/30 border border-gray-200 dark:border-slate-700/30">
            <MapPin size={16} className="shrink-0 mt-0.5" style={{ color: client.color || '#10b981' }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
                {typeof client.address === 'object' && client.address !== null
                  ? client.address.city || client.city || 'Ville inconnue'
                  : client.city || 'Ville inconnue'}
              </p>
              <p className="text-xs text-gray-400 dark:text-slate-500 truncate max-w-[180px]">
                {typeof client.address === 'object' && client.address !== null
                  ? `${client.address.street || ''} ${client.address.postalCode || ''}`.trim() || 'Adresse non renseignée'
                  : client.address || 'Adresse non renseignée'}
              </p>
            </div>
            {isValidAddress(client.address) && (() => {
              const { googleMapsFallback } = getNavigationLinks(formatAddress(client.address), client.addressNumber);
              return (
                <a
                  href={googleMapsFallback}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700/50 text-gray-400 hover:text-blue-500 transition-colors"
                  title="Naviguer vers cette adresse"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Navigation size={14} />
                </a>
              );
            })()}
          </div>

          <div className="flex gap-2">
            {client.phone && (
              <a href={`tel:${client.phone}`} className="flex-1 p-2 rounded-lg bg-gray-50 dark:bg-slate-900/30 border border-gray-200 dark:border-slate-700/30 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors hover:border-opacity-50" style={{ borderColor: 'transparent' }}>
                <Phone size={14} /> Tél
              </a>
            )}
            {client.email && (
              <a href={`mailto:${client.email}`} className="flex-1 p-2 rounded-lg bg-gray-50 dark:bg-slate-900/30 border border-gray-200 dark:border-slate-700/30 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors hover:border-opacity-50" style={{ borderColor: 'transparent' }}>
                <Mail size={14} /> Email
              </a>
            )}
          </div>
        </div>

        {/* Primary Action */}
        <button
          onClick={() => onAction('agenda', client)}
          className="w-full py-2.5 rounded-xl text-white font-medium shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2 relative overflow-hidden group"
          style={{
            backgroundColor: client.color || '#059669',
            boxShadow: `0 4px 14px -4px ${client.color || '#059669'}80`
          }}
        >
          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
          <Calendar size={18} />
          <span className="relative z-10">Voir l'agenda</span>
        </button>
      </div>
    </motion.div>
  );
};

import BeneficiaryAdminModal from '../modals/BeneficiaryAdminModal';

// --- Main Module ---

const BeneficiairesModule = ({
  filteredClients,
  clients,
  searchTermClients,
  showAdvancedFiltersClients,
  advancedFiltersClients,
  handleSearchClientsChange,
  toggleAdvancedFiltersClients,
  handleAdvancedFiltersClientsChange,
  hasPermission,
  appointments,
  employees,
  roles,
  getRoleName,
  onAction,
  onAddClient,
  db,
  currentUser,
  contracts = []
}) => {
  const { showToast } = useToastContext();
  const typesEntite = [...new Set(clients.map(client => client.typeEntite).filter(Boolean))];
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [clientForStatusChange, setClientForStatusChange] = useState(null);
  const [showGenerateDocModal, setShowGenerateDocModal] = useState(false);
  const [clientForDocGeneration, setClientForDocGeneration] = useState(null);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [clientForAssessment, setClientForAssessment] = useState(null);

  // New Admin Panel Modal State
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [clientForAdmin, setClientForAdmin] = useState(null);

  const filteredAppointments = useMemo(() => {
    if (!selectedClient) return [];
    return appointments.filter(apt => apt.clientId === selectedClient.id);
  }, [appointments, selectedClient]);

  const handleClientAction = (action, client) => {
    switch (action) {
      case 'schedule':
      case 'agenda':
        setSelectedClient(client);
        setShowAgendaModal(true);
        break;
      case 'changeStatus':
        setClientForStatusChange(client);
        setShowStatusModal(true);
        break;
      case 'generate-document':
        setClientForDocGeneration(client);
        setShowGenerateDocModal(true);
        break;
      case 'assessment':
        setClientForAssessment(client);
        setShowAssessmentModal(true);
        break;
      case 'admin-panel':
        console.log('Opening Admin Panel for:', client);
        setClientForAdmin(client);
        setShowAdminModal(true);
        break;
      default:
        // Pass basic actions (edit, delete) to parent
        if (onAction) {
          onAction(action, client);
        }
        break;
    }
  };

  const handleStatusChange = async (newStatus, data) => {
    if (!clientForStatusChange) return;
    try {
      const statusService = getBeneficiaryStatusService(db);
      await statusService.changeStatus(
        clientForStatusChange.id,
        newStatus,
        {
          ...data,
          userId: currentUser?.id || 'unknown',
          hasPermission: (check) => hasPermission(check)
        }
      );
      showToast('Statut modifié avec succès !', 'success');
      if (onAction) onAction('refresh');
      setShowStatusModal(false);
      setClientForStatusChange(null);
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      showToast(error.message || 'Erreur', 'error');
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-6 font-sans text-gray-900 dark:text-slate-100">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Premium Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl p-6 rounded-2xl border border-gray-200 dark:border-slate-700/50 shadow-xl">
          <div>
            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-400">
              Gestion des Bénéficiaires
            </h2>
            <p className="text-gray-500 dark:text-slate-400 mt-1">{filteredClients.length} dossiers actifs</p>
          </div>

          {hasPermission({ resource: 'beneficiaries', action: 'create' }) && (
            <button
              onClick={onAddClient}
              className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl shadow-lg shadow-emerald-500/20 font-bold transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
            >
              <UserPlus size={20} />
              Nouveau Dossier
            </button>
          )}
        </div>

        {/* Search & Filters */}
        <div className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-md p-4 rounded-2xl border border-gray-200 dark:border-slate-700/50">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400 dark:text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
              </div>
              <input
                type="text"
                value={searchTermClients}
                onChange={handleSearchClientsChange}
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 dark:border-slate-700 rounded-xl leading-5 bg-white dark:bg-slate-900/50 text-gray-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 sm:text-sm transition-colors"
                placeholder="Rechercher par nom, ville, dossier..."
              />
            </div>

            <button
              onClick={toggleAdvancedFiltersClients}
              className={`px-4 py-2 rounded-xl border flex items-center gap-2 font-medium transition-all ${showAdvancedFiltersClients
                ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400'
                : 'bg-gray-100 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              <SlidersHorizontal size={18} />
              Filtres
            </button>
          </div>

          <AnimatePresence>
            {showAdvancedFiltersClients && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700/50 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Status Filter */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase">Statut</label>
                    <select value={advancedFiltersClients.status} onChange={(e) => handleAdvancedFiltersClientsChange('status', e.target.value)} className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-slate-200 focus:border-emerald-500 outline-none">
                      <option value="all">Tous</option>
                      <option value={BENEFICIARY_STATUS.ACTIVE}>Actifs</option>
                      <option value={BENEFICIARY_STATUS.PENDING}>En attente</option>
                      <option value={BENEFICIARY_STATUS.INACTIVE}>Inactifs</option>
                      <option value={BENEFICIARY_STATUS.ARCHIVED}>Archivés</option>
                    </select>
                  </div>
                  {/* Access Filter */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase">Accès Portail</label>
                    <select value={advancedFiltersClients.hasAuthAccess || 'all'} onChange={(e) => handleAdvancedFiltersClientsChange('hasAuthAccess', e.target.value)} className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-slate-200 focus:border-emerald-500 outline-none">
                      <option value="all">Tous</option>
                      <option value="yes">Activé</option>
                      <option value="no">Désactivé</option>
                    </select>
                  </div>
                  {/* Entity Filter */}
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase">Type</label>
                    <select value={advancedFiltersClients.typeEntite} onChange={(e) => handleAdvancedFiltersClientsChange('typeEntite', e.target.value)} className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-slate-200 focus:border-emerald-500 outline-none">
                      <option value="all">Tous</option>
                      {typesEntite.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredClients.map((client, index) => (
              <PremiumBeneficiaryCard
                key={client.id}
                client={client}
                onAction={handleClientAction}
                hasPermission={hasPermission}
              />
            ))}
          </AnimatePresence>
        </div>

        {filteredClients.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 bg-gray-50 dark:bg-slate-800/30 rounded-3xl border border-dashed border-gray-200 dark:border-slate-700">
            <div className="w-20 h-20 bg-gray-200 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
              <User size={40} className="text-gray-400 dark:text-slate-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 dark:text-slate-300">Aucun bénéficiaire trouvé</h3>
            <p className="text-gray-400 dark:text-slate-500 mt-2">Essayez de modifier vos filtres ou ajoutez un nouveau dossier.</p>
          </div>
        )}
      </div>

      {/* Modals - Keeping Logic Intact */}
      {showAgendaModal && selectedClient && (
        <ClientAgendaView
          client={selectedClient}
          appointments={filteredAppointments}
          employees={employees}
          db={db}
          onClose={() => { setShowAgendaModal(false); setSelectedClient(null); }}
          hasPermission={hasPermission}
          onRefresh={() => onAction('refresh')}
        />
      )}

      {showStatusModal && clientForStatusChange && (
        <ChangeBeneficiaryStatusModal
          isOpen={showStatusModal}
          onClose={() => { setShowStatusModal(false); setClientForStatusChange(null); }}
          beneficiary={clients.find(c => c.id === clientForStatusChange.id) || clientForStatusChange}
          onConfirm={handleStatusChange}
          currentStatus={clientForStatusChange.status || BENEFICIARY_STATUS.ACTIVE}
        />
      )}

      {/* Document Modal (Simplified Wrapper) */}
      {showGenerateDocModal && clientForDocGeneration && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowGenerateDocModal(false)}>
          <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center bg-slate-800/50">
              <h2 className="text-xl font-bold text-white">Documents - {clientForDocGeneration.firstName} {clientForDocGeneration.lastName}</h2>
              <button onClick={() => setShowGenerateDocModal(false)} className="text-slate-400 hover:text-white p-2 hover:bg-slate-700 rounded-lg"><XCircle size={24} /></button>
            </div>
            <div className="flex-1 overflow-y-auto bg-slate-50 dark">
              <BeneficiaryDocumentGenerator
                db={db}
                user={currentUser}
                beneficiaryId={clientForDocGeneration.id}
                onClose={() => {
                  setShowGenerateDocModal(false);
                  setClientForDocGeneration(null);
                  showToast('Document généré !', 'success');
                  if (onAction) onAction('refresh');
                }}
                onCancel={() => { setShowGenerateDocModal(false); setClientForDocGeneration(null); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Assessment Modal (Wrapper) */}
      {showAssessmentModal && clientForAssessment && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAssessmentModal(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <BeneficiaryAssessmentWizard
              db={db}
              beneficiaryId={clientForAssessment.id}
              beneficiary={clientForAssessment}
              user={currentUser}
              onClose={() => { setShowAssessmentModal(false); setClientForAssessment(null); }}
              onComplete={() => {
                setShowAssessmentModal(false);
                setClientForAssessment(null);
                showToast('Évaluation terminée !', 'success');
                if (onAction) onAction('refresh');
              }}
            />
          </div>
        </div>
      )}

      {/* New Admin Panel Modal */}
      {showAdminModal && clientForAdmin && (
        <BeneficiaryAdminModal
          client={clientForAdmin}
          onClose={() => { setShowAdminModal(false); setClientForAdmin(null); }}
          onAction={handleClientAction}
          hasPermission={hasPermission}
        />
      )}

    </div>
  );
};

export default BeneficiairesModule;
